// breathingSystem.ts: Respiratory system that thresholds voice capacities in NeuroPsyMap
// The breathing system controls the creature's ability to vocalize based on respiratory state

import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { createPhysicalSoundSynthesizer, PhysicalSoundSynthesizer } from './physicalSoundSynthesis';
import { LifeBehaviorMathEngine, OscillatorState, HomeostaticParameters, MetabolicParameters } from './lifeBehaviorMath';
import { getAudioEngine } from './core/audio';

export interface BreathingState {
    // Core respiratory metrics
    oxygenLevel: number;           // 0-1, current oxygen saturation
    carbonDioxideLevel: number;    // 0-1, current CO2 levels
    lungCapacity: number;          // 0-1, current lung capacity
    respiratoryRate: number;       // breaths per minute
    breathDepth: number;           // 0-1, depth of current breath
    
    // Respiratory cycle state
    cyclePhase: 'inhale' | 'exhale' | 'pause_inhale' | 'pause_exhale';
    cycleProgress: number;         // 0-1, progress through current phase
    cycleDuration: number;         // ms, total cycle duration
    
    // Voice capacity thresholds
    voiceCapacity: number;         // 0-1, current voice capacity based on breathing
    maxVoiceDuration: number;      // ms, maximum continuous voice duration
    voiceIntensityLimit: number;   // 0-1, maximum voice intensity allowed
    
    // Respiratory health
    respiratoryEfficiency: number; // 0-1, overall respiratory health
    fatigueLevel: number;          // 0-1, respiratory fatigue
    stressLevel: number;           // 0-1, respiratory stress
    
    // Timing
    lastBreathTime: number;        // timestamp of last breath
    nextBreathTime: number;        // timestamp of next breath
    lastVoiceTime: number;         // timestamp of last vocalization
    voiceCooldownRemaining: number; // ms remaining in voice cooldown
    
    // Evolutionary breathing development
    breathingDevelopmentStage: 'embryonic' | 'fetal' | 'newborn' | 'infant' | 'child' | 'adolescent' | 'adult' | 'master';
    breathingMastery: number;      // 0-1, overall breathing mastery level
    breathingLessonsLearned: number; // number of breathing lessons completed
    breathingEfficiency: number;   // 0-1, learned breathing efficiency
    voiceUnlocked: boolean;        // whether voice is unlocked through breathing mastery
    breathingMilestones: BreathingMilestone[]; // achieved breathing milestones
    currentBreathingLesson: BreathingLesson | null; // current learning objective
    breathingInstincts: BreathingInstinct[]; // developed breathing instincts
}

export interface BreathingConfig {
    // Respiratory parameters
    baseRespiratoryRate: number;   // base breaths per minute
    baseLungCapacity: number;      // base lung capacity (0-1)
    oxygenConsumptionRate: number; // rate of oxygen consumption
    co2ProductionRate: number;     // rate of CO2 production
    
    // Voice capacity parameters
    voiceOxygenCost: number;       // oxygen consumed per voice unit
    voiceCo2Production: number;    // CO2 produced per voice unit
    maxVoiceIntensity: number;     // maximum voice intensity
    voiceRecoveryRate: number;     // rate of voice capacity recovery
    
    // Adaptation parameters
    adaptationRate: number;        // rate of adaptation to network state
    stressRecoveryRate: number;    // rate of stress recovery
    fatigueRecoveryRate: number;   // rate of fatigue recovery
    
    // Neural network influence
    networkEnergyInfluence: number; // how much network energy affects breathing
    networkComplexityInfluence: number; // how much network complexity affects breathing
    networkGrowthInfluence: number; // how much network growth affects breathing
}

export interface VoiceCapacityThresholds {
    canSpeak: boolean;             // whether creature can speak at all
    maxIntensity: number;          // maximum voice intensity (0-1)
    maxDuration: number;           // maximum continuous speech duration (ms)
    cooldownRequired: number;      // cooldown time before next speech (ms)
    breathRequired: boolean;       // whether a breath is required before speaking
    oxygenThreshold: number;       // minimum oxygen level required for speech
}

export interface BreathingMilestone {
    id: string;
    name: string;
    description: string;
    stage: 'embryonic' | 'fetal' | 'newborn' | 'infant' | 'child' | 'adolescent' | 'adult' | 'master';
    requirement: number;           // mastery level required (0-1)
    reward: string;                // what this milestone unlocks
    achieved: boolean;
    achievedAt: number;            // timestamp when achieved
}

export interface BreathingLesson {
    id: string;
    name: string;
    description: string;
    objective: string;             // what the creature needs to learn
    targetEfficiency: number;      // target breathing efficiency (0-1)
    targetConsistency: number;     // target breathing consistency (0-1)
    targetDepth: number;           // target breath depth (0-1)
    duration: number;              // how long to practice (ms)
    progress: number;              // current progress (0-1)
    completed: boolean;
    unlocksVoice: boolean;         // whether completing this lesson unlocks voice
}

export interface BreathingInstinct {
    id: string;
    name: string;
    description: string;
    trigger: string;               // what triggers this instinct
    response: string;              // how the creature responds
    efficiency: number;            // how efficient this instinct is (0-1)
    learned: boolean;
    strength: number;              // how strong this instinct is (0-1)
}

export interface AuditLogEntry {
    timestamp: number;
    system: string;
    action: string;
    details: string;
    duration?: number;
    state?: any;
}

export class BreathingSystem {
    private network: NeuralNetworkSimulator;
    private mathEngine: LifeBehaviorMathEngine; // Mathematical models for life behaviors
    private state: BreathingState;
    private config: BreathingConfig;
    private isActive: boolean = false;
    private lastUpdateTime: number = 0;
    
    // Audit logging system
    private auditEntries: AuditLogEntry[] = [];
    private maxAuditEntries: number = 1000;
    private systemStartTime: number = Date.now();
    
    // Mathematical oscillator states for breathing rhythm
    private inhaleOscillator: OscillatorState;
    private exhaleOscillator: OscillatorState;
    private homeostaticRegulators: Map<string, { integral: number, previousError: number }>;
    
    // Respiratory cycle tracking
    private cycleStartTime: number = 0;
    private phaseStartTime: number = 0;
    private currentPhaseDuration: number = 0;
    
    // Voice capacity tracking
    private currentVoiceDuration: number = 0;
    private voiceStartTime: number = 0;
    private isSpeaking: boolean = false;
    
    // Adaptive parameters
    private adaptiveRespiratoryRate: number = 0;
    private adaptiveLungCapacity: number = 0;
    private adaptiveEfficiency: number = 0;
    
    // Breathing sound generation with PHYSICAL MODELING
    private audioContext: AudioContext | null = null;
    private breathingGainNode: GainNode | null = null;
    private breathingFilterNode: BiquadFilterNode | null = null;
    private breathingNoiseNode: AudioBufferSourceNode | null = null;
    private isPlayingBreathSound: boolean = false;
    private lastBreathSoundTime: number = 0;
    private breathSoundCooldown: number = 100; // ms between breath sounds
    private breathingSoundEnabled: boolean = true;
    private breathingSoundVolume: number = 0.1; // Base volume for breathing sounds
    private physicalSynthesizer: PhysicalSoundSynthesizer | null = null; // PHYSICAL SOUND SYNTHESIS

    constructor(network: NeuralNetworkSimulator) {
        this.network = network;
        this.mathEngine = new LifeBehaviorMathEngine(); // Initialize mathematical models
        this.config = this.initializeConfig();
        this.state = this.initializeState();
        this.adaptiveRespiratoryRate = this.config.baseRespiratoryRate;
        this.adaptiveLungCapacity = this.config.baseLungCapacity;
        this.adaptiveEfficiency = 1.0;
        this.isActive = true;
        this.initializeBreathingAudio();
        
        // Initialize Van der Pol oscillators for breathing rhythm
        this.inhaleOscillator = {
            position: 0,
            velocity: 1.0,
            damping: 0.3,
            frequency: 0.2 * Math.PI, // Breathing frequency
            target: 0
        };
        this.exhaleOscillator = {
            position: 1.0,
            velocity: -1.0,
            damping: 0.3,
            frequency: 0.2 * Math.PI,
            target: 0
        };
        
        // Initialize homeostatic PID regulators for oxygen/CO2 balance
        this.homeostaticRegulators = new Map();
        this.homeostaticRegulators.set('oxygen', { integral: 0, previousError: 0 });
        this.homeostaticRegulators.set('co2', { integral: 0, previousError: 0 });
        this.homeostaticRegulators.set('lungCapacity', { integral: 0, previousError: 0 });
        
        this.auditLog('BREATHING_SYSTEM', 'INITIALIZATION', 'Breathing system initialized with mathematical life behavior models - voice locked until mastery achieved');
    }

    private initializeConfig(): BreathingConfig {
        return {
            // Respiratory parameters
            baseRespiratoryRate: 12,        // 12 breaths per minute
            baseLungCapacity: 0.8,          // 80% capacity
            oxygenConsumptionRate: 0.02,    // 2% per second
            co2ProductionRate: 0.015,       // 1.5% per second
            
            // Voice capacity parameters
            voiceOxygenCost: 0.1,           // 10% oxygen per voice unit
            voiceCo2Production: 0.08,       // 8% CO2 per voice unit
            maxVoiceIntensity: 0.9,         // 90% max intensity
            voiceRecoveryRate: 0.05,        // 5% recovery per second
            
            // Adaptation parameters
            adaptationRate: 0.01,           // 1% adaptation per update
            stressRecoveryRate: 0.02,       // 2% stress recovery per second
            fatigueRecoveryRate: 0.015,     // 1.5% fatigue recovery per second
            
            // Neural network influence
            networkEnergyInfluence: 0.3,    // 30% influence from network energy
            networkComplexityInfluence: 0.2, // 20% influence from network complexity
            networkGrowthInfluence: 0.1     // 10% influence from network growth
        };
    }

    private initializeState(): BreathingState {
        const now = Date.now();
        return {
            // Core respiratory metrics
            oxygenLevel: 0.95,              // Start with high oxygen
            carbonDioxideLevel: 0.05,       // Start with low CO2
            lungCapacity: this.config.baseLungCapacity,
            respiratoryRate: this.config.baseRespiratoryRate,
            breathDepth: 0.7,               // 70% depth
            
            // Respiratory cycle state
            cyclePhase: 'inhale',
            cycleProgress: 0.0,
            cycleDuration: 5000,            // 5 seconds per cycle
            
            // Voice capacity thresholds
            voiceCapacity: 0.0,             // No voice capacity initially - must learn to breathe first
            maxVoiceDuration: 0,            // No voice duration initially
            voiceIntensityLimit: 0.0,       // No voice intensity initially
            
            // Respiratory health
            respiratoryEfficiency: 0.1,     // Very low efficiency initially - must learn
            fatigueLevel: 0.0,              // No fatigue initially
            stressLevel: 0.0,               // No stress initially
            
            // Timing
            lastBreathTime: now,
            nextBreathTime: now + 5000,     // First breath in 5 seconds
            lastVoiceTime: 0,
            voiceCooldownRemaining: 0,
            
            // Evolutionary breathing development
            breathingDevelopmentStage: 'embryonic',
            breathingMastery: 0.0,          // No mastery initially
            breathingLessonsLearned: 0,
            breathingEfficiency: 0.1,       // Very low learned efficiency
            voiceUnlocked: false,           // Voice locked until breathing mastery
            breathingMilestones: this.initializeBreathingMilestones(),
            currentBreathingLesson: this.getNextBreathingLesson(),
            breathingInstincts: this.initializeBreathingInstincts()
        };
    }

    // Main update method - called every frame
    update(): void {
        if (!this.isActive) return;
        
        const updateStartTime = performance.now();
        const now = Date.now();
        const deltaTime = now - this.lastUpdateTime;
        this.lastUpdateTime = now;
        
        // Update respiratory cycle
        const cycleStartTime = performance.now();
        this.updateRespiratoryCycle(now, deltaTime);
        this.auditLog('BREATHING_SYSTEM', 'RESPIRATORY_CYCLE', `Updated respiratory cycle - phase: ${this.state.cyclePhase}`, performance.now() - cycleStartTime);
        
        // Update oxygen and CO2 levels
        const gasStartTime = performance.now();
        this.updateGasLevels(deltaTime);
        this.auditLog('BREATHING_SYSTEM', 'GAS_LEVELS', `O2: ${(this.state.oxygenLevel * 100).toFixed(1)}%, CO2: ${(this.state.carbonDioxideLevel * 100).toFixed(1)}%`, performance.now() - gasStartTime);
        
        // Update voice capacity based on breathing state
        const voiceStartTime = performance.now();
        this.updateVoiceCapacity(deltaTime);
        this.auditLog('BREATHING_SYSTEM', 'VOICE_CAPACITY', `Voice capacity: ${(this.state.voiceCapacity * 100).toFixed(1)}%, Unlocked: ${this.state.voiceUnlocked}`, performance.now() - voiceStartTime);
        
        // Adapt to network state
        const adaptStartTime = performance.now();
        this.adaptToNetworkState(deltaTime);
        this.auditLog('BREATHING_SYSTEM', 'NETWORK_ADAPTATION', `Adapted to network state`, performance.now() - adaptStartTime);
        
        // Update respiratory health
        const healthStartTime = performance.now();
        this.updateRespiratoryHealth(deltaTime);
        this.auditLog('BREATHING_SYSTEM', 'RESPIRATORY_HEALTH', `Efficiency: ${(this.state.respiratoryEfficiency * 100).toFixed(1)}%, Fatigue: ${(this.state.fatigueLevel * 100).toFixed(1)}%`, performance.now() - healthStartTime);
        
        // Update breathing development and learning
        const devStartTime = performance.now();
        this.updateBreathingDevelopment(deltaTime);
        this.auditLog('BREATHING_SYSTEM', 'BREATHING_DEVELOPMENT', `Stage: ${this.state.breathingDevelopmentStage}, Mastery: ${(this.state.breathingMastery * 100).toFixed(1)}%`, performance.now() - devStartTime);
        
        // Update timing
        this.updateTiming(now);
        
        // Log total update time
        this.auditLog('BREATHING_SYSTEM', 'UPDATE_COMPLETE', `Total update completed`, performance.now() - updateStartTime);
    }

    private updateRespiratoryCycle(now: number, deltaTime: number): void {
        const dt = deltaTime / 1000.0; // Convert to seconds
        const cycleProgress = (now - this.cycleStartTime) / this.state.cycleDuration;
        
        if (cycleProgress >= 1.0) {
            // Cycle complete, start new cycle
            this.startNewCycle(now);
            return;
        }
        
        this.state.cycleProgress = cycleProgress;
        
        // MATHEMATICAL MODEL: Use Van der Pol oscillator for natural breathing rhythm
        // Calculate inhale oscillator dynamics
        this.inhaleOscillator = this.mathEngine.oscillatorModel.calculateVanDerPolOscillator(
            this.inhaleOscillator, dt
        );
        
        // Calculate exhale oscillator dynamics (out of phase)
        this.exhaleOscillator = this.mathEngine.oscillatorModel.calculateVanDerPolOscillator(
            this.exhaleOscillator, dt
        );
        
        // Determine current phase based on oscillator dynamics
        const inhalePhase = (this.inhaleOscillator.position + 1) / 2; // Normalize to 0-1
        const exhalePhase = (this.exhaleOscillator.position + 1) / 2;
        
        // Combine oscillators for natural breathing pattern
        const breathingSignal = inhalePhase * (1 - exhalePhase * 0.5);
        
        // Determine current phase based on cycle progress (keeping logic for sound triggers)
        const inhaleDuration = 0.4;  // 40% of cycle
        const pauseInhaleDuration = 0.1;  // 10% of cycle
        const exhaleDuration = 0.4;  // 40% of cycle
        const pauseExhaleDuration = 0.1;  // 10% of cycle
        
        const previousPhase = this.state.cyclePhase;
        
        if (cycleProgress < inhaleDuration) {
            this.state.cyclePhase = 'inhale';
            // Use oscillator for natural breathing depth variation
            this.state.breathDepth = 0.3 + breathingSignal * 0.7;
        } else if (cycleProgress < inhaleDuration + pauseInhaleDuration) {
            this.state.cyclePhase = 'pause_inhale';
            this.state.breathDepth = 1.0; // Full capacity
        } else if (cycleProgress < inhaleDuration + pauseInhaleDuration + exhaleDuration) {
            this.state.cyclePhase = 'exhale';
            // Use oscillator for natural exhale pattern
            const exhaleProgress = (cycleProgress - inhaleDuration - pauseInhaleDuration) / exhaleDuration;
            this.state.breathDepth = 1.0 - exhaleProgress * 0.8 - (1 - breathingSignal) * 0.1;
        } else {
            this.state.cyclePhase = 'pause_exhale';
            this.state.breathDepth = 0.2; // 20% residual
        }
        
        // Play breathing sound when phase changes
        if (previousPhase !== this.state.cyclePhase) {
            this.playBreathingSound(this.state.cyclePhase);
        }
    }

    private startNewCycle(now: number): void {
        this.cycleStartTime = now;
        this.state.cycleProgress = 0.0;
        this.state.cyclePhase = 'inhale';
        this.state.lastBreathTime = now;
        this.state.nextBreathTime = now + this.state.cycleDuration;
        
        // Adjust cycle duration based on respiratory rate
        this.state.cycleDuration = (60 / this.state.respiratoryRate) * 1000; // Convert BPM to ms
    }

    private updateGasLevels(deltaTime: number): void {
        const seconds = deltaTime / 1000;
        
        // SCIENTIFIC MODEL: Fick's Law of Gas Diffusion
        // dV_O2/dt = D_L * A * (P_A_O2 - P_C_O2)
        // where D_L = diffusing capacity, A = surface area, P = partial pressure
        
        const D_L = 0.1; // mL/(min * mmHg) - lung diffusing capacity
        const A = this.state.lungCapacity * this.state.respiratoryEfficiency; // Alveolar surface area factor
        
        // Partial pressure calculations (mmHg)
        const P_O2_atmospheric = 160; // Standard atmospheric O2
        const P_CO2_atmospheric = 0.3; // Standard atmospheric CO2
        
        // Base oxygen consumption (metabolic demand)
        let oxygenConsumption = this.config.oxygenConsumptionRate * seconds;
        
        // Additional consumption during voice
        if (this.isSpeaking) {
            oxygenConsumption += this.config.voiceOxygenCost * (this.currentVoiceDuration / 1000);
        }
        
        // Oxygen diffusion during inhale (Fick's Law)
        if (this.state.cyclePhase === 'inhale') {
            const P_capillary_O2 = this.state.oxygenLevel * 160; // Capillary partial pressure
            const deltaP_O2 = P_O2_atmospheric - P_capillary_O2; // Partial pressure gradient
            
            // Fick's law: diffusion rate = D_L * A * deltaP
            const diffusion_O2 = D_L * A * deltaP_O2 * seconds / 60;
            const oxygenReplenishment = diffusion_O2 / 100; // Normalize
            
            this.state.oxygenLevel = Math.min(1.0, this.state.oxygenLevel + oxygenReplenishment - oxygenConsumption);
        } else {
            this.state.oxygenLevel = Math.max(0.0, this.state.oxygenLevel - oxygenConsumption);
        }
        
        // CO2 production (metabolic byproduct)
        let co2Production = this.config.co2ProductionRate * seconds;
        
        // Additional CO2 during voice
        if (this.isSpeaking) {
            co2Production += this.config.voiceCo2Production * (this.currentVoiceDuration / 1000);
        }
        
        // CO2 diffusion during exhale (Fick's Law - reverse direction)
        if (this.state.cyclePhase === 'exhale') {
            const P_capillary_CO2 = this.state.carbonDioxideLevel * 40; // Capillary partial pressure
            const deltaP_CO2 = P_capillary_CO2 - P_CO2_atmospheric; // Partial pressure gradient
            
            // Fick's law: diffusion rate = D_L * A * deltaP
            const diffusion_CO2 = D_L * A * deltaP_CO2 * seconds / 60;
            const co2Removal = diffusion_CO2 / 100; // Normalize
            
            this.state.carbonDioxideLevel = Math.max(0.0, this.state.carbonDioxideLevel + co2Production - co2Removal);
        } else {
            this.state.carbonDioxideLevel = Math.min(1.0, this.state.carbonDioxideLevel + co2Production);
        }
        
        // MATHEMATICAL MODEL: Homeostatic regulation using PID control
        // Regulate oxygen level toward optimal setpoint (0.95)
        const oxygenReg = this.homeostaticRegulators.get('oxygen')!;
        const oxygenParams: HomeostaticParameters = {
            currentValue: this.state.oxygenLevel,
            setPoint: 0.95,
            regulationStrength: 0.1,
            disturbance: -oxygenConsumption
        };
        const oxygenPID = this.mathEngine.homeostaticModel.calculatePIDControl(
            oxygenParams, seconds, oxygenReg.previousError, oxygenReg.integral
        );
        this.state.oxygenLevel = Math.max(0.0, Math.min(1.0, oxygenPID.value));
        oxygenReg.integral = oxygenPID.integral;
        oxygenReg.previousError = oxygenParams.setPoint - this.state.oxygenLevel;
        
        // Regulate CO2 level toward optimal setpoint (0.05)
        const co2Reg = this.homeostaticRegulators.get('co2')!;
        const co2Params: HomeostaticParameters = {
            currentValue: this.state.carbonDioxideLevel,
            setPoint: 0.05,
            regulationStrength: 0.08,
            disturbance: co2Production
        };
        const co2PID = this.mathEngine.homeostaticModel.calculatePIDControl(
            co2Params, seconds, co2Reg.previousError, co2Reg.integral
        );
        this.state.carbonDioxideLevel = Math.max(0.0, Math.min(1.0, co2PID.value));
        co2Reg.integral = co2PID.integral;
        co2Reg.previousError = co2Params.setPoint - this.state.carbonDioxideLevel;
        
        // MATHEMATICAL MODEL: Metabolic dynamics for energy management
        const metabolicParams: MetabolicParameters = {
            currentEnergy: this.state.respiratoryEfficiency,
            productionRate: this.state.breathDepth * 1.0,
            consumptionRate: 0.1 + this.state.stressLevel * 0.1 + this.state.fatigueLevel * 0.1,
            efficiencyFactor: this.state.breathingMastery,
            maxEnergy: 1.0
        };
        this.state.respiratoryEfficiency = this.mathEngine.metabolicModel.calculateEnergyDynamics(
            metabolicParams, seconds
        );
        
        // Ensure values stay within bounds
        this.state.oxygenLevel = Math.max(0.0, Math.min(1.0, this.state.oxygenLevel));
        this.state.carbonDioxideLevel = Math.max(0.0, Math.min(1.0, this.state.carbonDioxideLevel));
        this.state.respiratoryEfficiency = Math.max(0.0, Math.min(1.0, this.state.respiratoryEfficiency));
    }

    private updateVoiceCapacity(deltaTime: number): void {
        const seconds = deltaTime / 1000;
        
        // Base voice capacity recovery
        let capacityRecovery = this.config.voiceRecoveryRate * seconds;
        
        // Reduce recovery if oxygen is low or CO2 is high
        if (this.state.oxygenLevel < 0.7) {
            capacityRecovery *= this.state.oxygenLevel / 0.7; // Reduce recovery proportionally
        }
        if (this.state.carbonDioxideLevel > 0.3) {
            capacityRecovery *= (1.0 - this.state.carbonDioxideLevel) / 0.7; // Reduce recovery proportionally
        }
        
        // Reduce recovery if fatigued or stressed
        capacityRecovery *= (1.0 - this.state.fatigueLevel * 0.5);
        capacityRecovery *= (1.0 - this.state.stressLevel * 0.3);
        
        // Update voice capacity
        this.state.voiceCapacity = Math.min(1.0, this.state.voiceCapacity + capacityRecovery);
        
        // Update voice intensity limit based on breathing state
        this.state.voiceIntensityLimit = this.calculateVoiceIntensityLimit();
        
        // Update max voice duration based on oxygen and CO2 levels
        this.state.maxVoiceDuration = this.calculateMaxVoiceDuration();
        
        // Update cooldown
        if (this.state.voiceCooldownRemaining > 0) {
            this.state.voiceCooldownRemaining = Math.max(0, this.state.voiceCooldownRemaining - deltaTime);
        }
    }

    private calculateVoiceIntensityLimit(): number {
        let intensity = this.config.maxVoiceIntensity;
        
        // Reduce intensity if oxygen is low
        if (this.state.oxygenLevel < 0.8) {
            intensity *= this.state.oxygenLevel / 0.8;
        }
        
        // Reduce intensity if CO2 is high
        if (this.state.carbonDioxideLevel > 0.2) {
            intensity *= (1.0 - this.state.carbonDioxideLevel * 0.5);
        }
        
        // Reduce intensity if fatigued
        intensity *= (1.0 - this.state.fatigueLevel * 0.4);
        
        // Reduce intensity if stressed
        intensity *= (1.0 - this.state.stressLevel * 0.3);
        
        return Math.max(0.1, Math.min(1.0, intensity));
    }

    private calculateMaxVoiceDuration(): number {
        let duration = 3000; // Base 3 seconds
        
        // Reduce duration if oxygen is low
        if (this.state.oxygenLevel < 0.8) {
            duration *= this.state.oxygenLevel / 0.8;
        }
        
        // Reduce duration if CO2 is high
        if (this.state.carbonDioxideLevel > 0.2) {
            duration *= (1.0 - this.state.carbonDioxideLevel * 0.6);
        }
        
        // Reduce duration if fatigued
        duration *= (1.0 - this.state.fatigueLevel * 0.5);
        
        // Reduce duration if stressed
        duration *= (1.0 - this.state.stressLevel * 0.4);
        
        return Math.max(500, Math.min(10000, duration)); // Between 0.5 and 10 seconds
    }

    private adaptToNetworkState(deltaTime: number): void {
        if (this.network.nodes.length === 0) return;
        
        const seconds = deltaTime / 1000;
        const adaptationRate = this.config.adaptationRate * seconds;
        
        // Calculate network influences
        const avgEnergy = this.network.nodes.reduce((sum, node) => sum + (node.energy || 0), 0) / this.network.nodes.length;
        const networkComplexity = this.network.linguisticComplexity;
        const networkGrowth = this.network.nodes.length / 100; // Normalize growth
        
        // Breathing development influences network growth
        this.applyBreathingInfluenceToNetwork(seconds);
        
        // Adapt respiratory rate based on network energy
        const targetRespiratoryRate = this.config.baseRespiratoryRate + 
            (avgEnergy - 0.5) * this.config.networkEnergyInfluence * 10;
        this.adaptiveRespiratoryRate += (targetRespiratoryRate - this.adaptiveRespiratoryRate) * adaptationRate;
        this.state.respiratoryRate = Math.max(6, Math.min(30, this.adaptiveRespiratoryRate));
        
        // Adapt lung capacity based on network complexity
        const targetLungCapacity = this.config.baseLungCapacity + 
            (networkComplexity - 1) * this.config.networkComplexityInfluence * 0.2;
        this.adaptiveLungCapacity += (targetLungCapacity - this.adaptiveLungCapacity) * adaptationRate;
        this.state.lungCapacity = Math.max(0.3, Math.min(1.0, this.adaptiveLungCapacity));
        
        // Adapt efficiency based on network growth
        const targetEfficiency = 1.0 + (networkGrowth - 0.5) * this.config.networkGrowthInfluence * 0.3;
        this.adaptiveEfficiency += (targetEfficiency - this.adaptiveEfficiency) * adaptationRate;
        this.state.respiratoryEfficiency = Math.max(0.5, Math.min(1.5, this.adaptiveEfficiency));
    }

    private applyBreathingInfluenceToNetwork(seconds: number): void {
        // Breathing mastery influences neural network development
        const breathingMastery = this.state.breathingMastery;
        const breathingStage = this.state.breathingDevelopmentStage;
        
        // Higher breathing mastery allows for more complex neural development
        if (breathingMastery > 0.3) {
            // Increase node growth rate based on breathing mastery
            const breathingBoost = breathingMastery * 0.1; // Up to 10% boost
            this.network.limbGrowthRate = Math.min(0.2, this.network.limbGrowthRate + breathingBoost * seconds);
        }
        
        // Breathing efficiency affects neural network energy
        if (this.state.breathingEfficiency > 0.5) {
            // Boost node energy based on breathing efficiency
            const energyBoost = this.state.breathingEfficiency * 0.05; // Up to 5% energy boost
            this.network.nodes.forEach(node => {
                if (node.energy !== undefined) {
                    node.energy = Math.min(1.0, node.energy + energyBoost * seconds);
                }
            });
        }
        
        // Voice unlock enables linguistic development
        if (this.state.voiceUnlocked && !this.network.creatureVoice) {
            // Voice system becomes more active when breathing is mastered
            this.network.linguisticComplexity = Math.max(this.network.linguisticComplexity, 1.5);
        }
        
        // Breathing milestones trigger neural network evolution
        const recentMilestones = this.state.breathingMilestones.filter(m => 
            m.achieved && (Date.now() - m.achievedAt) < 5000 // Within last 5 seconds
        );
        
        for (const milestone of recentMilestones) {
            this.triggerNeuralEvolutionFromBreathing(milestone);
        }
    }

    private triggerNeuralEvolutionFromBreathing(milestone: BreathingMilestone): void {
        switch (milestone.id) {
            case 'first_breath':
                // First breath triggers initial neural activity
                this.network.nodes.forEach(node => {
                    if (node.energy !== undefined) {
                        node.energy = Math.min(1.0, node.energy + 0.1);
                    }
                });
                break;
            case 'rhythmic_breathing':
                // Rhythmic breathing enables pattern recognition
                this.network.linguisticComplexity = Math.max(this.network.linguisticComplexity, 1.2);
                break;
            case 'deep_breathing':
                // Deep breathing increases neural capacity
                this.network.limbGrowthRate = Math.min(0.15, this.network.limbGrowthRate + 0.02);
                break;
            case 'breathing_control':
                // Breathing control unlocks advanced neural functions
                this.network.linguisticComplexity = Math.max(this.network.linguisticComplexity, 2.0);
                break;
            case 'efficient_breathing':
                // Efficient breathing enables sustained neural activity
                this.network.nodes.forEach(node => {
                    if (node.energy !== undefined) {
                        node.energy = Math.min(1.0, node.energy + 0.05);
                    }
                });
                break;
            case 'adaptive_breathing':
                // Adaptive breathing enables complex neural patterns
                this.network.linguisticComplexity = Math.max(this.network.linguisticComplexity, 3.0);
                break;
            case 'master_breathing':
                // Master breathing unlocks full neural potential
                this.network.linguisticComplexity = Math.max(this.network.linguisticComplexity, 5.0);
                this.network.limbGrowthRate = Math.min(0.25, this.network.limbGrowthRate + 0.05);
                break;
        }
        
        console.log(`[Breathing Evolution] Neural network evolved from breathing milestone: ${milestone.name}`);
    }

    private updateRespiratoryHealth(deltaTime: number): void {
        const seconds = deltaTime / 1000;
        
        // Update fatigue based on voice usage
        if (this.isSpeaking) {
            this.state.fatigueLevel = Math.min(1.0, this.state.fatigueLevel + 0.01 * seconds);
        } else {
            this.state.fatigueLevel = Math.max(0.0, this.state.fatigueLevel - this.config.fatigueRecoveryRate * seconds);
        }
        
        // Update stress based on oxygen and CO2 levels
        const oxygenStress = this.state.oxygenLevel < 0.6 ? (0.6 - this.state.oxygenLevel) / 0.6 : 0;
        const co2Stress = this.state.carbonDioxideLevel > 0.4 ? (this.state.carbonDioxideLevel - 0.4) / 0.6 : 0;
        const totalStress = oxygenStress + co2Stress;
        
        if (totalStress > 0) {
            this.state.stressLevel = Math.min(1.0, this.state.stressLevel + totalStress * 0.02 * seconds);
        } else {
            this.state.stressLevel = Math.max(0.0, this.state.stressLevel - this.config.stressRecoveryRate * seconds);
        }
        
        // Update respiratory efficiency based on health
        const healthFactor = (1.0 - this.state.fatigueLevel * 0.3) * (1.0 - this.state.stressLevel * 0.2);
        this.state.respiratoryEfficiency = Math.max(0.3, Math.min(1.5, healthFactor));
    }

    private updateTiming(now: number): void {
        // Update voice cooldown
        if (this.state.voiceCooldownRemaining > 0) {
            this.state.voiceCooldownRemaining = Math.max(0, this.state.voiceCooldownRemaining - (now - this.lastUpdateTime));
        }
        
        // Update last voice time
        if (this.isSpeaking) {
            this.state.lastVoiceTime = now;
        }
    }

    // Breathing development and learning methods
    private initializeBreathingMilestones(): BreathingMilestone[] {
        return [
            {
                id: 'first_breath',
                name: 'First Breath',
                description: 'The creature takes its first conscious breath',
                stage: 'embryonic',
                requirement: 0.1,
                reward: 'Basic breathing awareness',
                achieved: false,
                achievedAt: 0
            },
            {
                id: 'rhythmic_breathing',
                name: 'Rhythmic Breathing',
                description: 'The creature develops consistent breathing rhythm',
                stage: 'fetal',
                requirement: 0.3,
                reward: 'Improved oxygen efficiency',
                achieved: false,
                achievedAt: 0
            },
            {
                id: 'deep_breathing',
                name: 'Deep Breathing',
                description: 'The creature learns to take deep, controlled breaths',
                stage: 'newborn',
                requirement: 0.5,
                reward: 'Enhanced lung capacity',
                achieved: false,
                achievedAt: 0
            },
            {
                id: 'breathing_control',
                name: 'Breathing Control',
                description: 'The creature gains conscious control over breathing',
                stage: 'infant',
                requirement: 0.7,
                reward: 'Voice capacity unlocked',
                achieved: false,
                achievedAt: 0
            },
            {
                id: 'efficient_breathing',
                name: 'Efficient Breathing',
                description: 'The creature masters efficient breathing patterns',
                stage: 'child',
                requirement: 0.8,
                reward: 'Extended voice duration',
                achieved: false,
                achievedAt: 0
            },
            {
                id: 'adaptive_breathing',
                name: 'Adaptive Breathing',
                description: 'The creature adapts breathing to different situations',
                stage: 'adolescent',
                requirement: 0.9,
                reward: 'Dynamic voice modulation',
                achieved: false,
                achievedAt: 0
            },
            {
                id: 'master_breathing',
                name: 'Master Breathing',
                description: 'The creature achieves complete breathing mastery',
                stage: 'master',
                requirement: 1.0,
                reward: 'Full voice potential unlocked',
                achieved: false,
                achievedAt: 0
            }
        ];
    }

    private initializeBreathingInstincts(): BreathingInstinct[] {
        return [
            {
                id: 'survival_breath',
                name: 'Survival Breathing',
                description: 'Automatic breathing when oxygen is low',
                trigger: 'oxygen < 0.3',
                response: 'Increase breathing rate and depth',
                efficiency: 0.6,
                learned: false,
                strength: 0.0
            },
            {
                id: 'calm_breathing',
                name: 'Calm Breathing',
                description: 'Slow, deep breathing when stressed',
                trigger: 'stress > 0.7',
                response: 'Slow down breathing rate',
                efficiency: 0.8,
                learned: false,
                strength: 0.0
            },
            {
                id: 'energy_breathing',
                name: 'Energy Breathing',
                description: 'Energetic breathing for high activity',
                trigger: 'network energy > 0.8',
                response: 'Increase breathing efficiency',
                efficiency: 0.9,
                learned: false,
                strength: 0.0
            },
            {
                id: 'voice_breathing',
                name: 'Voice Breathing',
                description: 'Coordinated breathing for speech',
                trigger: 'voice attempt',
                response: 'Prepare breath for speech',
                efficiency: 0.95,
                learned: false,
                strength: 0.0
            }
        ];
    }

    private getNextBreathingLesson(): BreathingLesson | null {
        const lessons = [
            {
                id: 'basic_rhythm',
                name: 'Basic Rhythm',
                description: 'Learn to breathe in a consistent pattern',
                objective: 'Maintain steady breathing rhythm for 30 seconds',
                targetEfficiency: 0.3,
                targetConsistency: 0.5,
                targetDepth: 0.4,
                duration: 30000,
                progress: 0.0,
                completed: false,
                unlocksVoice: false
            },
            {
                id: 'breath_depth',
                name: 'Breath Depth',
                description: 'Learn to control breath depth',
                objective: 'Take deep breaths consistently',
                targetEfficiency: 0.5,
                targetConsistency: 0.7,
                targetDepth: 0.8,
                duration: 45000,
                progress: 0.0,
                completed: false,
                unlocksVoice: false
            },
            {
                id: 'breath_control',
                name: 'Breath Control',
                description: 'Gain conscious control over breathing',
                objective: 'Control breathing rate and depth consciously',
                targetEfficiency: 0.7,
                targetConsistency: 0.8,
                targetDepth: 0.9,
                duration: 60000,
                progress: 0.0,
                completed: false,
                unlocksVoice: true
            },
            {
                id: 'voice_coordination',
                name: 'Voice Coordination',
                description: 'Learn to coordinate breathing with voice',
                objective: 'Breathe in preparation for speech',
                targetEfficiency: 0.8,
                targetConsistency: 0.9,
                targetDepth: 0.95,
                duration: 90000,
                progress: 0.0,
                completed: false,
                unlocksVoice: true
            }
        ];

        // Find the first incomplete lesson
        return lessons.find(lesson => !lesson.completed) || null;
    }

    private updateBreathingDevelopment(deltaTime: number): void {
        const seconds = deltaTime / 1000;
        
        // Update breathing mastery based on current performance
        this.updateBreathingMastery(seconds);
        
        // Check for milestone achievements
        this.checkMilestoneAchievements();
        
        // Update current breathing lesson
        this.updateCurrentBreathingLesson(seconds);
        
        // Develop breathing instincts
        this.developBreathingInstincts(seconds);
        
        // Update development stage
        this.updateDevelopmentStage();
        
        // Update voice unlock status
        this.updateVoiceUnlockStatus();
    }

    private updateBreathingMastery(seconds: number): void {
        // Calculate mastery based on current breathing performance
        const efficiency = this.state.respiratoryEfficiency;
        const consistency = this.calculateBreathingConsistency();
        const depth = this.state.breathDepth;
        
        // Mastery increases based on sustained good performance
        const performanceScore = (efficiency + consistency + depth) / 3;
        // Increased learning rate by 50x for better progression (was 0.001, now 0.05)
        const masteryIncrease = performanceScore * 0.05 * seconds;
        
        this.state.breathingMastery = Math.min(1.0, this.state.breathingMastery + masteryIncrease);
        
        // Update learned efficiency
        this.state.breathingEfficiency = Math.min(1.0, this.state.breathingEfficiency + masteryIncrease * 0.5);
        
        // Log mastery progress periodically
        if (this.state.breathingMastery > 0.001 && Math.random() < 0.001) { // 0.1% chance per update
            console.log(`[Breathing Development] Mastery: ${(this.state.breathingMastery * 100).toFixed(2)}% (Stage: ${this.state.breathingDevelopmentStage})`);
        }
    }

    private calculateBreathingConsistency(): number {
        // Calculate how consistent the breathing rhythm is
        const targetRate = this.state.respiratoryRate;
        const actualRate = 60000 / this.state.cycleDuration; // Convert ms to BPM
        const rateConsistency = 1.0 - Math.abs(targetRate - actualRate) / targetRate;
        
        // Calculate phase consistency (how well it follows the cycle)
        const phaseConsistency = this.state.cycleProgress > 0.9 || this.state.cycleProgress < 0.1 ? 1.0 : 0.8;
        
        return (rateConsistency + phaseConsistency) / 2;
    }

    private checkMilestoneAchievements(): void {
        for (const milestone of this.state.breathingMilestones) {
            if (!milestone.achieved && this.state.breathingMastery >= milestone.requirement) {
                milestone.achieved = true;
                milestone.achievedAt = Date.now();
                console.log(`[Breathing Development] Milestone achieved: ${milestone.name} - ${milestone.reward}`);
                
                // Apply milestone rewards
                this.applyMilestoneReward(milestone);
            }
        }
    }

    private applyMilestoneReward(milestone: BreathingMilestone): void {
        switch (milestone.id) {
            case 'first_breath':
                this.state.respiratoryEfficiency = Math.max(this.state.respiratoryEfficiency, 0.2);
                break;
            case 'rhythmic_breathing':
                this.state.respiratoryEfficiency = Math.max(this.state.respiratoryEfficiency, 0.4);
                break;
            case 'deep_breathing':
                this.state.lungCapacity = Math.max(this.state.lungCapacity, 0.6);
                break;
            case 'breathing_control':
                this.state.voiceCapacity = Math.max(this.state.voiceCapacity, 0.3);
                this.state.voiceUnlocked = true;
                break;
            case 'efficient_breathing':
                this.state.maxVoiceDuration = Math.max(this.state.maxVoiceDuration, 2000);
                break;
            case 'adaptive_breathing':
                this.state.voiceIntensityLimit = Math.max(this.state.voiceIntensityLimit, 0.8);
                break;
            case 'master_breathing':
                this.state.voiceCapacity = 1.0;
                this.state.maxVoiceDuration = 10000;
                this.state.voiceIntensityLimit = 1.0;
                break;
        }
    }

    private updateCurrentBreathingLesson(seconds: number): void {
        if (!this.state.currentBreathingLesson) return;
        
        const lesson = this.state.currentBreathingLesson;
        
        // Check if lesson objectives are being met
        const efficiencyMet = this.state.breathingEfficiency >= lesson.targetEfficiency;
        const consistencyMet = this.calculateBreathingConsistency() >= lesson.targetConsistency;
        const depthMet = this.state.breathDepth >= lesson.targetDepth;
        
        if (efficiencyMet && consistencyMet && depthMet) {
            // Increase lesson progress
            lesson.progress = Math.min(1.0, lesson.progress + 0.01 * seconds);
            
            // Check if lesson is completed
            if (lesson.progress >= 1.0 && !lesson.completed) {
                lesson.completed = true;
                this.state.breathingLessonsLearned++;
                
                if (lesson.unlocksVoice) {
                    this.state.voiceUnlocked = true;
                    console.log(`[Breathing Development] Voice unlocked through lesson: ${lesson.name}`);
                }
                
                console.log(`[Breathing Development] Lesson completed: ${lesson.name}`);
                
                // Get next lesson
                this.state.currentBreathingLesson = this.getNextBreathingLesson();
            }
        }
    }

    private developBreathingInstincts(seconds: number): void {
        for (const instinct of this.state.breathingInstincts) {
            if (!instinct.learned) {
                // Check if instinct trigger conditions are met
                const shouldLearn = this.checkInstinctTrigger(instinct);
                
                if (shouldLearn) {
                    // Gradually learn this instinct
                    instinct.strength = Math.min(1.0, instinct.strength + 0.002 * seconds);
                    
                    if (instinct.strength >= 0.8) {
                        instinct.learned = true;
                        console.log(`[Breathing Development] Instinct learned: ${instinct.name}`);
                    }
                }
            }
        }
    }

    private checkInstinctTrigger(instinct: BreathingInstinct): boolean {
        switch (instinct.id) {
            case 'survival_breath':
                return this.state.oxygenLevel < 0.3;
            case 'calm_breathing':
                return this.state.stressLevel > 0.7;
            case 'energy_breathing':
                return this.network.nodes.length > 0 && 
                       this.network.nodes.reduce((sum, node) => sum + (node.energy || 0), 0) / this.network.nodes.length > 0.8;
            case 'voice_breathing':
                return this.state.voiceUnlocked && this.isSpeaking;
            default:
                return false;
        }
    }

    private updateDevelopmentStage(): void {
        const mastery = this.state.breathingMastery;
        let newStage = this.state.breathingDevelopmentStage;
        
        if (mastery < 0.1) newStage = 'embryonic';
        else if (mastery < 0.2) newStage = 'fetal';
        else if (mastery < 0.3) newStage = 'newborn';
        else if (mastery < 0.5) newStage = 'infant';
        else if (mastery < 0.7) newStage = 'child';
        else if (mastery < 0.9) newStage = 'adolescent';
        else if (mastery < 1.0) newStage = 'adult';
        else newStage = 'master';
        
        if (newStage !== this.state.breathingDevelopmentStage) {
            console.log(`[Breathing Development] Stage evolved: ${this.state.breathingDevelopmentStage} → ${newStage}`);
            this.state.breathingDevelopmentStage = newStage;
        }
    }

    private updateVoiceUnlockStatus(): void {
        // Voice is unlocked when breathing control milestone is achieved
        const breathingControlMilestone = this.state.breathingMilestones.find(m => m.id === 'breathing_control');
        if (breathingControlMilestone && breathingControlMilestone.achieved) {
            this.state.voiceUnlocked = true;
        }
    }

    // Audit logging methods
    private auditLog(system: string, action: string, details: string, duration?: number, state?: any): void {
        const entry: AuditLogEntry = {
            timestamp: Date.now(),
            system,
            action,
            details,
            duration,
            state: state ? JSON.parse(JSON.stringify(state)) : undefined
        };
        
        this.auditEntries.push(entry);
        
        // Keep only the most recent entries
        if (this.auditEntries.length > this.maxAuditEntries) {
            this.auditEntries = this.auditEntries.slice(-this.maxAuditEntries);
        }
        
        // Only log to console for critical events to avoid spam
        const criticalActions = ['INITIALIZATION', 'ERROR', 'VOICE_UNLOCKED'];
        if (criticalActions.includes(action)) {
            const timeSinceStart = Date.now() - this.systemStartTime;
            const durationStr = duration ? ` (${duration.toFixed(2)}ms)` : '';
            console.log(`[AUDIT ${timeSinceStart}ms] ${system}.${action}: ${details}${durationStr}`);
        }
    }

    getAuditLog(): AuditLogEntry[] {
        return [...this.auditEntries];
    }

    getAuditLogBySystem(system: string): AuditLogEntry[] {
        return this.auditEntries.filter(entry => entry.system === system);
    }

    getAuditLogByAction(action: string): AuditLogEntry[] {
        return this.auditEntries.filter(entry => entry.action === action);
    }

    clearAuditLog(): void {
        this.auditEntries = [];
        this.auditLog('BREATHING_SYSTEM', 'AUDIT_CLEAR', 'Audit log cleared');
    }

    // Voice capacity management
    getVoiceCapacityThresholds(): VoiceCapacityThresholds {
        // Voice is only available if unlocked through breathing mastery
        const canSpeak = this.state.voiceUnlocked &&
                        this.state.voiceCapacity > 0.1 && 
                        this.state.oxygenLevel > 0.3 && 
                        this.state.carbonDioxideLevel < 0.7 &&
                        this.state.voiceCooldownRemaining <= 0;
        
        // Always require breath for voice production
        const breathRequired = true;
        
        const thresholds = {
            canSpeak,
            maxIntensity: this.state.voiceIntensityLimit,
            maxDuration: this.state.maxVoiceDuration,
            cooldownRequired: this.state.voiceCooldownRemaining,
            breathRequired,
            oxygenThreshold: 0.3
        };
        
        this.auditLog('BREATHING_SYSTEM', 'VOICE_THRESHOLDS', `Voice thresholds calculated - canSpeak: ${canSpeak}, unlocked: ${this.state.voiceUnlocked}, capacity: ${(this.state.voiceCapacity * 100).toFixed(1)}%`);
        
        return thresholds;
    }

    // Voice usage tracking
    startVoice(): boolean {
        const thresholds = this.getVoiceCapacityThresholds();
        
        if (!thresholds.canSpeak) {
            console.log('[Breathing System] Voice blocked - insufficient capacity');
            return false;
        }
        
        this.isSpeaking = true;
        this.voiceStartTime = Date.now();
        this.currentVoiceDuration = 0;
        
        console.log('[Breathing System] Voice started');
        return true;
    }

    updateVoice(deltaTime: number): boolean {
        if (!this.isSpeaking) return false;
        
        this.currentVoiceDuration += deltaTime;
        
        // Check if voice duration exceeded
        if (this.currentVoiceDuration > this.state.maxVoiceDuration) {
            this.stopVoice();
            return false;
        }
        
        // Check if oxygen too low or CO2 too high
        if (this.state.oxygenLevel < 0.2 || this.state.carbonDioxideLevel > 0.8) {
            this.stopVoice();
            return false;
        }
        
        return true;
    }

    stopVoice(): void {
        if (!this.isSpeaking) return;
        
        this.isSpeaking = false;
        this.state.voiceCooldownRemaining = 1000; // 1 second cooldown
        this.state.lastVoiceTime = Date.now();
        
        // Reduce voice capacity based on usage
        const usageFactor = this.currentVoiceDuration / this.state.maxVoiceDuration;
        this.state.voiceCapacity = Math.max(0.0, this.state.voiceCapacity - usageFactor * 0.3);
        
        console.log('[Breathing System] Voice stopped');
    }

    // Breathing control methods
    forceBreath(): void {
        this.startNewCycle(Date.now());
        console.log('[Breathing System] Forced breath');
    }

    adjustRespiratoryRate(rate: number): void {
        this.state.respiratoryRate = Math.max(6, Math.min(30, rate));
        console.log(`[Breathing System] Respiratory rate adjusted to ${rate} BPM`);
    }

    adjustLungCapacity(capacity: number): void {
        this.state.lungCapacity = Math.max(0.3, Math.min(1.0, capacity));
        console.log(`[Breathing System] Lung capacity adjusted to ${(capacity * 100).toFixed(1)}%`);
    }

    // State access methods
    getBreathingState(): BreathingState {
        return { ...this.state };
    }

    getBreathingConfig(): BreathingConfig {
        return { ...this.config };
    }

    // Restore state for save/load functionality
    restoreState(savedState: BreathingState): void {
        try {
            this.state = { ...savedState };
            console.log('[Breathing System] State restored successfully');
        } catch (error) {
            console.warn('[Breathing System] Failed to restore state:', error);
        }
    }

    // Health status
    getHealthStatus(): { status: 'excellent' | 'good' | 'fair' | 'poor' | 'critical', message: string } {
        const oxygen = this.state.oxygenLevel;
        const co2 = this.state.carbonDioxideLevel;
        const fatigue = this.state.fatigueLevel;
        const stress = this.state.stressLevel;
        
        if (oxygen < 0.3 || co2 > 0.8) {
            return { status: 'critical', message: 'Critical respiratory distress - immediate attention required' };
        } else if (oxygen < 0.5 || co2 > 0.6 || fatigue > 0.8 || stress > 0.8) {
            return { status: 'poor', message: 'Poor respiratory health - rest recommended' };
        } else if (oxygen < 0.7 || co2 > 0.4 || fatigue > 0.6 || stress > 0.6) {
            return { status: 'fair', message: 'Fair respiratory health - monitor closely' };
        } else if (oxygen > 0.8 && co2 < 0.3 && fatigue < 0.4 && stress < 0.4) {
            return { status: 'excellent', message: 'Excellent respiratory health' };
        } else {
            return { status: 'good', message: 'Good respiratory health' };
        }
    }

    // Breathing audio methods
    private initializeBreathingAudio(): void {
        try {
            // Use the network's existing audio context
            this.audioContext = this.network.audioContext;
            
            if (this.audioContext && this.audioContext.state === 'running') {
                this.setupBreathingAudioNodes();
                
                // Initialize PHYSICAL SOUND SYNTHESIZER
                this.physicalSynthesizer = createPhysicalSoundSynthesizer(this.audioContext, this.network);
                console.log('[Breathing System] Audio initialized with PHYSICAL MODELING');
            } else {
                // Only log audio not ready once, don't spam
                // console.log('[Breathing System] Audio context not ready, will retry later');
                this.audioContext = null;
            }
        } catch (error) {
            console.warn('[Breathing System] Audio initialization failed:', error);
            this.audioContext = null;
        }
    }

    private setupBreathingAudioNodes(): void {
        if (!this.audioContext) return;
        
        // Create breathing sound processing chain
        this.breathingGainNode = this.audioContext.createGain();
        this.breathingGainNode.gain.value = 0.001; // Reduced to prevent clipping
        
        this.breathingFilterNode = this.audioContext.createBiquadFilter();
        this.breathingFilterNode.type = 'bandpass';
        this.breathingFilterNode.frequency.value = 200; // Low frequency for breathing
        this.breathingFilterNode.Q.value = 1.0;
        
        // Connect breathing audio chain
        this.breathingGainNode.connect(this.breathingFilterNode);
        try {
            const engine = getAudioEngine();
            if (engine.context === this.audioContext) {
                this.breathingFilterNode.connect(engine.getBusGain('ambience'));
            } else {
                const out = this.network && (this.network as any).getAudioOutput ? this.network.getAudioOutput() : this.audioContext.destination;
                this.breathingFilterNode.connect(out);
            }
        } catch {
            const out = this.network && (this.network as any).getAudioOutput ? this.network.getAudioOutput() : this.audioContext.destination;
            this.breathingFilterNode.connect(out);
        }
    }

    private playBreathingSound(phase: 'inhale' | 'exhale' | 'pause_inhale' | 'pause_exhale'): void {
        // Check if breathing sounds are enabled
        if (!this.breathingSoundEnabled) {
            return;
        }
        
        // Try to initialize audio if not already done
        if (!this.audioContext && this.network.audioContext && this.network.audioContext.state === 'running') {
            this.audioContext = this.network.audioContext;
            this.setupBreathingAudioNodes();
            
            // Initialize physical synthesizer
            if (!this.physicalSynthesizer) {
                this.physicalSynthesizer = createPhysicalSoundSynthesizer(this.audioContext, this.network);
            }
        }
        
        if (!this.audioContext || !this.breathingGainNode || !this.breathingFilterNode) {
            return;
        }
        
        if (this.isPlayingBreathSound) {
            return; // Prevent overlapping breath sounds
        }
        
        const now = Date.now();
        if (now - this.lastBreathSoundTime < this.breathSoundCooldown) {
            return; // Respect cooldown
        }
        
        this.lastBreathSoundTime = now;
        this.isPlayingBreathSound = true;
        
        try {
            // Get breath depth from current state
            const breathDepth = this.state.breathDepth;
            const duration = this.getBreathingSoundDuration(phase);
            
            let buffer: AudioBuffer;
            
            // Use PHYSICAL MODELING if synthesizer is available
            if (this.physicalSynthesizer) {
                console.log(`[Breathing System] Using PHYSICAL MODELING for ${phase} sound`);
                buffer = this.physicalSynthesizer.synthesizeBreathingSound(phase, breathDepth, duration);
            } else {
                // Fallback to simple noise (should not happen if properly initialized)
                console.warn('[Breathing System] Physical synthesizer not available, using fallback');
                const sampleRate = this.audioContext.sampleRate;
                const bufferLength = sampleRate * duration;
                buffer = this.audioContext.createBuffer(1, bufferLength, sampleRate);
                const data = buffer.getChannelData(0);
                
                // Generate breathing noise (fallback)
                for (let i = 0; i < bufferLength; i++) {
                    data[i] = (Math.random() * 2 - 1) * this.getBreathingPhaseVolume(phase);
                }
            }
            
            // Create and configure audio source
            const source = this.audioContext.createBufferSource();
            source.buffer = buffer;
            
            // Configure filter based on breathing phase (still needed for additional shaping)
            this.configureBreathingFilter(phase);
            
            // Configure gain based on breath depth and health
            const volume = this.getBreathingPhaseVolume(phase) * this.breathingSoundVolume;
            this.breathingGainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
            
            // Connect and play
            source.connect(this.breathingGainNode);
            source.start(this.audioContext.currentTime);
            
            // Clean up when finished
            source.onended = () => {
                this.isPlayingBreathSound = false;
            };
            
            console.log(`[Breathing System] Playing ${phase} sound with PHYSICAL MODELING`);
        } catch (error) {
            console.warn('[Breathing System] Failed to play breathing sound:', error);
            this.isPlayingBreathSound = false;
        }
    }

    private getBreathingSoundDuration(phase: 'inhale' | 'exhale' | 'pause_inhale' | 'pause_exhale'): number {
        const baseDuration = 0.3; // 300ms base duration
        
        switch (phase) {
            case 'inhale':
                return baseDuration * this.state.breathDepth; // Longer for deeper breaths
            case 'exhale':
                return baseDuration * this.state.breathDepth * 1.2; // Exhale is typically longer
            case 'pause_inhale':
            case 'pause_exhale':
                return baseDuration * 0.3; // Shorter for pauses
            default:
                return baseDuration;
        }
    }

    private getBreathingPhaseVolume(phase: 'inhale' | 'exhale' | 'pause_inhale' | 'pause_exhale'): number {
        const baseVolume = 0.05; // Low base volume
        const breathDepthMultiplier = this.state.breathDepth;
        const healthMultiplier = this.state.respiratoryEfficiency;
        
        let volume = baseVolume * breathDepthMultiplier * healthMultiplier;
        
        // Adjust volume based on phase
        switch (phase) {
            case 'inhale':
                volume *= 0.8; // Inhale is quieter
                break;
            case 'exhale':
                volume *= 1.2; // Exhale is louder
                break;
            case 'pause_inhale':
            case 'pause_exhale':
                volume *= 0.3; // Pauses are very quiet
                break;
        }
        
        // Reduce volume if stressed or fatigued
        volume *= (1.0 - this.state.stressLevel * 0.3);
        volume *= (1.0 - this.state.fatigueLevel * 0.2);
        
        return Math.max(0.01, Math.min(0.2, volume)); // Clamp between 1% and 20%
    }

    private configureBreathingFilter(phase: 'inhale' | 'exhale' | 'pause_inhale' | 'pause_exhale'): void {
        if (!this.breathingFilterNode) return;
        
        const now = this.audioContext!.currentTime;
        
        switch (phase) {
            case 'inhale':
                // Inhale: lower frequency, more resonant
                this.breathingFilterNode.frequency.setValueAtTime(150, now);
                this.breathingFilterNode.Q.setValueAtTime(1.5, now);
                break;
            case 'exhale':
                // Exhale: higher frequency, more breathy
                this.breathingFilterNode.frequency.setValueAtTime(300, now);
                this.breathingFilterNode.Q.setValueAtTime(0.8, now);
                break;
            case 'pause_inhale':
                // Pause inhale: very low frequency, subtle
                this.breathingFilterNode.frequency.setValueAtTime(100, now);
                this.breathingFilterNode.Q.setValueAtTime(2.0, now);
                break;
            case 'pause_exhale':
                // Pause exhale: low frequency, very subtle
                this.breathingFilterNode.frequency.setValueAtTime(80, now);
                this.breathingFilterNode.Q.setValueAtTime(1.0, now);
                break;
        }
    }

    // Breathing sound control methods
    setBreathingSoundEnabled(enabled: boolean): void {
        this.breathingSoundEnabled = enabled;
        console.log(`[Breathing System] Sounds ${enabled ? 'enabled' : 'disabled'}`);
    }

    setBreathingSoundVolume(volume: number): void {
        this.breathingSoundVolume = Math.max(0, Math.min(1, volume));
        console.log(`[Breathing System] Volume set to ${(this.breathingSoundVolume * 100).toFixed(0)}%`);
    }

    getBreathingSoundEnabled(): boolean {
        return this.breathingSoundEnabled;
    }

    getBreathingSoundVolume(): number {
        return this.breathingSoundVolume;
    }

    // System control
    pause(): void {
        this.isActive = false;
        console.log('[Breathing System] Paused');
    }

    resume(): void {
        this.isActive = true;
        this.lastUpdateTime = Date.now();
        console.log('[Breathing System] Resumed');
    }

    reset(): void {
        this.state = this.initializeState();
        this.adaptiveRespiratoryRate = this.config.baseRespiratoryRate;
        this.adaptiveLungCapacity = this.config.baseLungCapacity;
        this.adaptiveEfficiency = 1.0;
        this.isSpeaking = false;
        this.currentVoiceDuration = 0;
        this.voiceStartTime = 0;
        console.log('[Breathing System] Reset');
    }
}

// Factory function to create breathing system
export function createBreathingSystem(network: NeuralNetworkSimulator): BreathingSystem {
    return new BreathingSystem(network);
}
